import React, { useEffect, useState, useCallback } from 'react'
import PropTypes, { any } from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  useGridApiRef,
  DataGridPremium,
  gridFilteredSortedRowIdsSelector,
} from '@mui/x-data-grid-premium'
import moment from 'moment'
import { Box, Grid, Paper } from '../../../../../components/mui/Core/index'
import LinkButton from '../../../../../components/LinkButton/link-button'
import {
  resetParentFeature,
  resetSelectedItemTag,
  resetSerialNumbers,
  setParentFeature,
  setSelectedItemTag,
} from '../../../../SerialNumbers/serial-numbers-slice'
import { getNumberValueFormat } from '../../../../../utils/string-utils'
import ItemOutOfStock from './components/ItemOutOfStock/item-out-of-stock'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'
import { parentFeatureConstants } from '../../../../../constants/parent-feature-constants'
import Constants from '../../../../../constants/en.json'
import GenericTablePagination from '../../../../../components/GenericTablePagination/generic-table-pagination'
import TableToolBar from '../../../../../components/TableToolBar/table-toolbar'

export function ItemTagTable({ filterBy, filterValue, searchByOptions }) {
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  })
  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''
  const filterByType = selectedSearchOption[0]?.type
  const noResultsValue = filterByType === 'date' ? moment.utc(filterValue).format('L') : filterValue
  const itemTags = useSelector((state) => state.itemTag.itemTags)
  const isFetchingItemTags = useSelector((state) => state.itemTag.isFetchingItemTags)
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState(() => new Set())
  const [detailsByRowId, setDetailsByRowId] = useState({})
  const [loadingRowIds, setLoadingRowIds] = useState(new Set())
  const [isRowExpandPermission, setIsRowExpandPermission] = useState('YES')

  const handleMultipleLinkClick = (tag) => {
    dispatch(resetSelectedItemTag())
    dispatch(setSelectedItemTag(tag))
    dispatch(resetParentFeature())
    dispatch(resetSerialNumbers())
    dispatch(setParentFeature(parentFeatureConstants.INVENTORY))
    navigate('/serialnumbers')
  }

  const rows = itemTags.map((row) => ({
    id: row.index,
    tag: row.tag,
    quantity: row.quantity,
    uom: row.uom,
    lot: row.lot,
    serialNumbers: row.serialNumbers,
    expirationDate: row.expirationDate,
    warehouse: row.warehouse,
    location: row.location,
    itemCode: row.itemCode,
  }))
  console.log(detailsByRowId)
  const handleDetailPanelExpandedRowIdsChange = useCallback(
    (newIds) => {
      console.log('🔍 Expand button clicked. newIds:', newIds)
      setDetailPanelExpandedRowIds(newIds)

      // Find newly expanded rows
      const newlyExpanded = [...newIds].filter((id) => !detailPanelExpandedRowIds.has(id))

      // Call API for each newly expanded row
      newlyExpanded.forEach((rowId) => {
        if (!detailsByRowId[rowId]) {
          // capture the row data from the current rows array
          const rowData = rows.find((r) => r.id === rowId)

          setLoadingRowIds((prev) => new Set([...prev, rowId]))
          console.log(`📡 Fetching details for rowId: ${rowId}`, 'rowData:', rowData)

          setDetailsByRowId((prev) => ({
            ...prev,
            [rowId]: rowId,
          }))
          // fetch(`/api/item-tags/${rowId}`)
          //   .then((res) => res.json())
          //   .then((apiData) => {
          //     console.log(`API response for rowId ${rowId}:`, apiData)
          //     // store both the row data and the API response together
          //     setDetailsByRowId((prev) => ({
          //       ...prev,
          //       [rowId]: { row: rowData, api: apiData },
          //     }))
          //   })
          //   .catch((err) => {
          //     console.error(`API error for rowId ${rowId}:`, err)
          //   })
          //   .finally(() => {
          //     setLoadingRowIds((prev) => {
          //       const updated = new Set(prev)
          //       updated.delete(rowId)
          //       return updated
          //     })
          //   })
          setLoadingRowIds((prev) => {
            const updated = new Set(prev)
            updated.delete(rowId)
            return updated
          })
        }
      })
    },
    [detailPanelExpandedRowIds, detailsByRowId],
  )

  const getDetailPanelContent = useCallback(
    ({ row }) => {
      const isLoading = loadingRowIds.has(row.id)
      const stored = detailsByRowId[row.id]

      if (isLoading) {
        return <Box sx={{ p: 2 }}>Loading details...</Box>
      }

      if (!stored) {
        return <Box sx={{ p: 2 }}>No data available</Box>
      }

      const rowData = stored.row || row
      const apiData = stored.api

      return (
        <Box sx={{ p: 2 }}>
          <div>
            <strong>Tag:</strong> {rowData?.tag}
          </div>
          <div style={{ marginTop: 8 }}>
            <strong>API Data:</strong>
            <pre>{JSON.stringify(apiData, null, 2)}</pre>
          </div>
        </Box>
      )
    },
    [detailsByRowId, loadingRowIds],
  )

  const getDetailPanelHeight = () => 'auto'

  const [filteredRowsCount, setFilteredRowsCount] = useState(rows.length)

  const updateFilteredRowCount = useCallback(() => {
    const filteredRowIds = gridFilteredSortedRowIdsSelector(apiRef) || []
    setFilteredRowsCount(filteredRowIds?.length || 0)
  }, [apiRef])

  useEffect(() => {
    if (!apiRef.current) return undefined

    updateFilteredRowCount()

    const unsubscribeFilter = apiRef.current.subscribeEvent(
      'filterModelChange',
      updateFilteredRowCount,
    )
    return () => unsubscribeFilter()
  }, [apiRef, rows, updateFilteredRowCount])

  const columns = [
    { field: 'tag', headerName: 'Tag', flex: 0.5, minWidth: 100 },
    {
      field: 'quantity',
      headerName: Constants.itemTagTable.QTY,
      flex: 0.45,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => {
        return getNumberValueFormat(value)
      },
      minWidth: 80,
    },
    { field: 'uom', headerName: Constants.itemTagTable.UOM, flex: 0.3, minWidth: 80 },
    { field: 'lot', headerName: Constants.itemTagTable.LOT_NUMBER, flex: 0.8, minWidth: 100 },
    {
      field: 'serialNumbers',
      headerName: Constants.itemTagTable.SERIAL_NUMBER,
      flex: 1,
      minWidth: 100,
      valueGetter: (value) => {
        const serialNumbers = value
        if (serialNumbers?.length === 0) {
          return 'None'
        }
        if (serialNumbers?.length === 1) {
          return serialNumbers[0]
        }
        if (serialNumbers?.length > 1) {
          return 'Multiple'
        }
        return value
      },
      renderCell: (cellValues) => {
        const serialNumbers = cellValues.row?.serialNumbers
        if (serialNumbers?.length === 0) {
          return 'None'
        }
        if (serialNumbers?.length === 1) {
          return serialNumbers[0]
        }
        if (serialNumbers?.length > 1) {
          return (
            <LinkButton
              handleLinkClick={() => handleMultipleLinkClick(cellValues.row)}
              linkText='Multiple'
            />
          )
        }
        return cellValues
      },
    },
    {
      field: 'expirationDate',
      headerName: Constants.itemTagTable.EXPIRATION,
      flex: 0.5,
      minWidth: 110,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (value) => value,
      valueFormatter: (value) => value && moment.utc(value).format('L'),
    },
    { field: 'warehouse', headerName: Constants.itemTagTable.WAREHOUSE, flex: 0.4, minWidth: 110 },
    { field: 'location', headerName: Constants.itemTagTable.LOCATION, flex: 0.4, minWidth: 110 },
    { field: 'itemCode', headerName: Constants.itemTagTable.ITEM_CODE, flex: 1, minWidth: 110 },
  ]

  const filterModel = {
    items: [{ field: filterBy, operator: 'contains', value: filterValue }],
  }

  const handleChangeRowsPerPage = (e) => {
    setPaginationModel({ page: 0, pageSize: parseInt(e.target.value, 10) })
  }
  const handleChangePage = (e, newPage) => {
    setPaginationModel((prev) => ({ ...prev, page: newPage }))
  }

  const itemTagTableStyle = {
    minHeight: '160px',
    height: isFetchingItemTags ? '400px' : 'auto',
  }

  return (
    <Grid sx={{ paddingTop: 3 }}>
      {rows.length === 0 && !isFetchingItemTags ? (
        <ItemOutOfStock />
      ) : (
        <Paper>
          {/* Client pagination */}
          <DataGridPremium
            apiRef={apiRef}
            sx={itemTagTableStyle}
            rows={rows}
            columns={columns}
            getDetailPanelContent={
              isRowExpandPermission === 'YES' ? getDetailPanelContent : undefined
            }
            getDetailPanelHeight={
              isRowExpandPermission === 'YES' ? getDetailPanelHeight : undefined
            }
            detailPanelExpandedRowIds={
              isRowExpandPermission === 'YES' ? detailPanelExpandedRowIds : undefined
            }
            onDetailPanelExpandedRowIdsChange={
              isRowExpandPermission === 'YES' ? handleDetailPanelExpandedRowIdsChange : undefined
            }
            loading={isFetchingItemTags}
            disableColumnFilter
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25, page: 0 },
              },
            }}
            pageSizeOptions={[25, 50]}
            spacing={1}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            slotProps={{
              toolbar: { rows },
              pagination: {
                count: filteredRowsCount,
                page: paginationModel.page,
                rowsPerPage: paginationModel.pageSize,
                onPageChange: handleChangePage,
                onRowsPerPageChange: handleChangeRowsPerPage,
              },
              noResultsOverlay: {
                filterByDescription,
                noResultsValue,
              },
            }}
            pagination
            slots={{
              noResultsOverlay: NoResultsFoundTableOverlay,
              toolbar: TableToolBar,
              pagination: GenericTablePagination,
            }}
            filterModel={filterModel}
            disableRowSelectionOnClick
            showToolbar
            disableColumnReorder
          />
        </Paper>
      )}
    </Grid>
  )
}

ItemTagTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(any)).isRequired,
}

export default ItemTagTable
